import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import type { DestinationContext } from "@/lib/dashboard-home/destinations";
import { pickerPageGroups } from "@/lib/dashboard-home/shortcut-picker";
import {
  MAX_SHORTCUT_LABEL_LENGTH,
  newCustomShortcutId,
  normalizeInternalPath,
  shortcutLabel,
  type StoredShortcut,
} from "@/lib/dashboard-home/shortcuts";
import { ArrowLeft, ClipboardPaste } from "lucide-react";
import { useState } from "react";
import { HomeSearchInput } from "../../HomeSearchInput";
import { PickerRow } from "./PickerRow";

/**
 * Onglet « Une page » : un tableau du back-office choisi dans la liste, ou
 * n'importe quelle page (filtrée, triée…) dont on colle l'adresse.
 */
export const PageTab = ({
  shortcuts,
  context,
  full,
  onAdd,
}: {
  shortcuts: StoredShortcut[];
  context: DestinationContext;
  full: boolean;
  onAdd: (shortcut: StoredShortcut) => void;
}) => {
  const vt = useVisibleT();
  const [pasteMode, setPasteMode] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");

  const groups = pickerPageGroups(shortcuts, context, query, vt);
  const pastedPath = normalizeInternalPath(address);
  const path = pasteMode ? pastedPath : selectedPath;
  const name = shortcutLabel(label);
  const canAdd = !full && path !== null && name.length > 0;

  const add = () => {
    if (!canAdd || !path) return;
    onAdd({ id: newCustomShortcutId(), custom: { label: name, path } });
  };

  const nameAndAdd = (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 p-3 sm:flex-row sm:items-center">
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={vt("bo.home.shortcuts.customLabel", "Nom du raccourci")}
        aria-label={vt("bo.home.shortcuts.customLabel", "Nom du raccourci")}
        maxLength={MAX_SHORTCUT_LABEL_LENGTH}
        onKeyDown={(e) => e.key === "Enter" && add()}
      />
      <Button type="button" onClick={add} disabled={!canAdd}>
        {vt("bo.home.shortcuts.add", "Ajouter")}
      </Button>
    </div>
  );

  if (pasteMode) {
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setPasteMode(false)}
          className="flex w-fit items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {vt("bo.home.shortcuts.backToPages", "Choisir dans la liste")}
        </button>
        <p className="text-sm text-slate-600">
          {vt(
            "bo.home.shortcuts.pasteExplain",
            "Ouvrez la page voulue (avec ses filtres ou son tri), copiez son adresse dans la barre du navigateur et collez-la ici.",
          )}
        </p>
        <Input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={vt(
            "bo.home.shortcuts.customPath",
            "Adresse de la page (copiée depuis le navigateur)",
          )}
          autoFocus
        />
        {address.trim() !== "" && pastedPath === null && (
          <p className="text-sm text-red-600">
            {vt(
              "bo.home.shortcuts.customPathInvalid",
              "Seules les pages de ce back-office peuvent être ajoutées.",
            )}
          </p>
        )}
        {nameAndAdd}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <HomeSearchInput
        autoFocus
        value={query}
        onChange={setQuery}
        placeholder={vt("bo.home.shortcuts.searchPage", "Rechercher une page")}
      />
      {groups.map((group) => (
        <section key={group.id} className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {group.label}
          </h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {group.pages.map(({ page, label: pageLabel, used }) => (
              <PickerRow
                key={page.id}
                icon={page.icon}
                label={pageLabel}
                used={used}
                selected={page.path === selectedPath}
                disabled={full}
                onSelect={() => {
                  setSelectedPath(page.path);
                  setLabel(pageLabel);
                }}
              />
            ))}
          </div>
          {/* Le nom se règle juste sous la page choisie. */}
          {group.pages.some((p) => p.page.path === selectedPath) && nameAndAdd}
        </section>
      ))}
      {groups.length === 0 && (
        <p className="text-sm text-slate-500">
          {vt(
            "bo.home.shortcuts.noPage",
            "Aucune page ne correspond à votre recherche.",
          )}
        </p>
      )}
      <button
        type="button"
        onClick={() => {
          setPasteMode(true);
          setLabel("");
        }}
        className="flex w-fit items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <ClipboardPaste className="size-4" aria-hidden />
        {vt(
          "bo.home.shortcuts.pasteLink",
          "Une page filtrée ou une autre page ? Collez son adresse",
        )}
      </button>
    </div>
  );
};
